import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../../api/client";
import Toast from "../../components/Toast";

// Searchable dropdown backed by a master list, with inline "add new" — mirrors
// the customer/location picker on CreatePO.jsx.
function AddableSelect({ value, onChange, items, setItems, createItem, noun, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const ref = useRef(null);

  const selected = items.find((c) => c.id === value) || null;

  const filtered = items.filter((c) => {
    const q = query.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q);
  });

  const showAdd =
    query.trim().length > 0 &&
    !items.some((c) => c.name.toLowerCase() === query.trim().toLowerCase());

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleFocus = () => {
    setQuery("");
    setOpen(true);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    if (value) onChange("");
    setOpen(true);
  };

  const handleSelect = (item) => {
    onChange(item.id);
    setQuery(item.name);
    setOpen(false);
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    setCreateError("");
    try {
      const created = await createItem(name);
      setItems((prev) => [...prev, created]);
      onChange(created.id);
      setQuery(created.name);
      setOpen(false);
    } catch (err) {
      setCreateError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        `Failed to create ${noun}`
      );
    } finally {
      setCreating(false);
    }
  };

  const displayValue = open ? query : (selected ? selected.name : query);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        style={inputStyle}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 300,
          background: "#fff", border: "1px solid #d6dfed", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden",
        }}>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.length === 0 && !showAdd && (
              <div style={{ padding: "10px 14px", fontSize: 13, color: "#94a3b8" }}>No {noun}s found</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(c)}
                style={{
                  width: "100%", padding: "9px 14px", border: "none",
                  background: c.id === value ? "#eff6ff" : "transparent",
                  cursor: "pointer", textAlign: "left", fontSize: 13,
                  color: c.id === value ? "#1d4ed8" : "#0f2d5c", fontWeight: c.id === value ? 600 : 400,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
                onMouseEnter={(e) => { if (c.id !== value) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = c.id === value ? "#eff6ff" : "transparent"; }}
              >
                {c.name}
                {c.id === value && <span style={{ fontSize: 12, color: "#1d4ed8" }}>✓</span>}
              </button>
            ))}
            {showAdd && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCreate}
                disabled={creating}
                style={{
                  width: "100%", padding: "9px 14px", border: "none",
                  borderTop: filtered.length > 0 ? "1px solid #f1f5f9" : "none",
                  background: "transparent", cursor: "pointer", textAlign: "left",
                  fontSize: 13, color: "#1d4ed8", fontWeight: 600,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#eff6ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {creating ? "Adding…" : `+ Add "${query.trim()}" as new ${noun}`}
              </button>
            )}
            {createError && (
              <div style={{ padding: "6px 14px", fontSize: 12, color: "#dc2626" }}>{createError}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const API_UPLOAD_URL = process.env.REACT_APP_API_UPLOAD_URL || "";

const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #d6dfed",
  borderRadius: 8, fontSize: 14, boxSizing: "border-box",
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 4 };

function LeadTimePreview({ baseQuantity, baseLeadTime }) {
  if (!baseQuantity || !baseLeadTime) return null;
  const bq = Number(baseQuantity);
  const bl = Number(baseLeadTime);
  if (!bq || !bl) return null;
  const rows = [1, 2, 3, 4].map((multiplier) => ({
    from: multiplier === 1 ? 1 : (multiplier - 1) * bq + 1,
    to: multiplier * bq,
    days: multiplier * bl,
  }));
  return (
    <div style={{ marginTop: 10, background: "#f8fafc", border: "1px solid #e4ebf5", borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>
        Auto-calculated schedule
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {rows.map((r) => (
          <div key={r.to} style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 10px", fontSize: 12 }}>
            <span style={{ color: "#64748b" }}>{r.from.toLocaleString()}–{r.to.toLocaleString()} units</span>
            <span style={{ fontWeight: 700, color: "#1d4ed8", marginLeft: 6 }}>{r.days}d</span>
          </div>
        ))}
        <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#94a3b8" }}>
          and so on…
        </div>
      </div>
    </div>
  );
}

function StockRangeBar({ current, min, max }) {
  if (min == null && max == null) return null;
  const upper = max != null ? max : Math.max(min * 2, current, 1);
  const pct = Math.min(100, Math.round((current / upper) * 100));
  const belowMin = min != null && current <= min;
  return (
    <div style={{ marginTop: 4, width: 100 }}>
      <div style={{ height: 6, borderRadius: 999, background: "#f1f5f9", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: belowMin ? "#dc2626" : "#16a34a", borderRadius: 999 }} />
      </div>
    </div>
  );
}

function PartModal({ part, categories, setCategories, locations, setLocations, suppliers, setSuppliers, onClose, onSaved }) {
  const [name, setName] = useState(part?.name || "");
  const [code, setCode] = useState(part?.code || "");
  const [baseQuantity, setBaseQuantity] = useState(part?.baseQuantity ?? "");
  const [baseLeadTime, setBaseLeadTime] = useState(part?.baseLeadTime ?? "");
  const [imageFile, setImageFile] = useState(null);
  const [clearImage, setClearImage] = useState(false);
  const [categoryId, setCategoryId] = useState(part?.categoryId || "");
  const [locationId, setLocationId] = useState(part?.locationId || "");
  const [supplierId, setSupplierId] = useState(part?.supplierId || "");
  const [unitOfMeasure, setUnitOfMeasure] = useState(part?.unitOfMeasure || "pcs");
  const [minStockLevel, setMinStockLevel] = useState(part?.minStockLevel ?? "");
  const [maxStockLevel, setMaxStockLevel] = useState(part?.maxStockLevel ?? "");
  const [reorderQty, setReorderQty] = useState(part?.reorderQty ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("code", code.trim());
      if (baseQuantity !== "") fd.append("baseQuantity", baseQuantity);
      if (baseLeadTime !== "") fd.append("baseLeadTime", baseLeadTime);
      if (imageFile) fd.append("image", imageFile);
      if (clearImage && !imageFile) fd.append("clearImage", "true");
      fd.append("unitOfMeasure", unitOfMeasure || "pcs");
      fd.append("categoryId", categoryId || "");
      fd.append("locationId", locationId || "");
      fd.append("supplierId", supplierId || "");
      if (minStockLevel !== "") fd.append("minStockLevel", minStockLevel);
      if (maxStockLevel !== "") fd.append("maxStockLevel", maxStockLevel);
      if (reorderQty !== "") fd.append("reorderQty", reorderQty);

      if (part) {
        await api.patch(`/manufacturing/parts/${part.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/manufacturing/parts", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save part");
    } finally {
      setSaving(false);
    }
  };

  const existingImage = part?.imageFileName ? `${API_UPLOAD_URL}${part.imageFileName}` : null;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#fff", borderRadius: 14, padding: 28, width: 560,
        maxHeight: "90vh", overflow: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
      }}>
        <h2 style={{ margin: "0 0 20px", fontSize: 18, color: "#0f2d5c" }}>
          {part ? "Edit Part" : "Add New Part"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
            <div>
              <label style={labelStyle}>Part Name *</label>
              <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Gear" />
            </div>
            <div>
              <label style={labelStyle}>Part Code *</label>
              <input style={inputStyle} value={code} onChange={(e) => setCode(e.target.value)} required placeholder="e.g. GEAR-001" />
            </div>
          </div>

          {/* Lead time */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 8 }}>Lead Time</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={labelStyle}>Base Quantity</label>
                <input
                  style={inputStyle}
                  type="number" min="1"
                  placeholder="e.g. 500"
                  value={baseQuantity}
                  onChange={(e) => setBaseQuantity(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Days for Base Qty</label>
                <input
                  style={inputStyle}
                  type="number" min="1"
                  placeholder="e.g. 20"
                  value={baseLeadTime}
                  onChange={(e) => setBaseLeadTime(e.target.value)}
                />
              </div>
            </div>
            <LeadTimePreview baseQuantity={baseQuantity} baseLeadTime={baseLeadTime} />
          </div>

          {/* Inventory */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 8 }}>Inventory</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Category</label>
                <AddableSelect
                  value={categoryId}
                  onChange={setCategoryId}
                  items={categories}
                  setItems={setCategories}
                  createItem={async (name) => (await api.post("/manufacturing/inventory/categories", { name })).data.data}
                  noun="category"
                  placeholder="Search or add category…"
                />
              </div>
              <div>
                <label style={labelStyle}>Location</label>
                <AddableSelect
                  value={locationId}
                  onChange={setLocationId}
                  items={locations}
                  setItems={setLocations}
                  createItem={async (name) => (await api.post("/manufacturing/inventory/locations", { name })).data.data}
                  noun="location"
                  placeholder="Search or add location…"
                />
              </div>
              <div>
                <label style={labelStyle}>Supplier</label>
                <AddableSelect
                  value={supplierId}
                  onChange={setSupplierId}
                  items={suppliers}
                  setItems={setSuppliers}
                  createItem={async (name) => (await api.post("/manufacturing/inventory/suppliers", { name })).data.data}
                  noun="supplier"
                  placeholder="Search or add supplier…"
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <div>
                <label style={labelStyle}>Unit of Measure</label>
                <input style={inputStyle} value={unitOfMeasure} onChange={(e) => setUnitOfMeasure(e.target.value)} placeholder="pcs" />
              </div>
              <div>
                <label style={labelStyle}>Min Stock Level</label>
                <input style={inputStyle} type="number" min="0" value={minStockLevel} onChange={(e) => setMinStockLevel(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Max Stock Level</label>
                <input style={inputStyle} type="number" min="0" value={maxStockLevel} onChange={(e) => setMaxStockLevel(e.target.value)} />
              </div>
            </div>
            {part && (
              <div style={{ marginTop: 12, display: "flex", gap: 20, fontSize: 13, color: "#64748b" }}>
                <span>Current Stock: <strong style={{ color: "#0f2d5c" }}>{part.currentStock ?? 0}</strong></span>
                <span>Current Price: <strong style={{ color: "#0f2d5c" }}>{part.unitCost != null ? `₹${part.unitCost}` : "—"}</strong></span>
                <span style={{ color: "#94a3b8" }}>Stock changes only via Log Movement, not here.</span>
              </div>
            )}
          </div>

          {/* Image */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Part Image (optional)</label>
            {existingImage && !clearImage && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <img src={existingImage} alt="part" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid #e4ebf5" }} />
                <button type="button" onClick={() => { setClearImage(true); setImageFile(null); }} style={{ fontSize: 12, color: "#dc2626", background: "none", border: "none", cursor: "pointer" }}>
                  Remove image
                </button>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ fontSize: 13 }} onChange={(e) => { setImageFile(e.target.files[0] || null); setClearImage(false); }} />
          </div>

          {error && <p style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</p>}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #d6dfed", background: "#f8fafc", cursor: "pointer", fontSize: 14 }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{ padding: "8px 20px", borderRadius: 8, background: "#1d4ed8", color: "#fff", border: "none", cursor: saving ? "default" : "pointer", fontWeight: 700, fontSize: 14, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Save Part"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PartsMaster() {
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [modal, setModal] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const partsResp = await api.get("/manufacturing/parts", { params: { includeArchived: true } });
      setParts(partsResp.data.data || []);
    } catch {
      setToast({ msg: "Failed to load parts", type: "error" });
    } finally {
      setLoading(false);
    }

    const [catResp, locResp, supResp] = await Promise.allSettled([
      api.get("/manufacturing/inventory/categories"),
      api.get("/manufacturing/inventory/locations"),
      api.get("/manufacturing/inventory/suppliers"),
    ]);
    if (catResp.status === "fulfilled") setCategories(catResp.value.data.data || []);
    if (locResp.status === "fulfilled") setLocations(locResp.value.data.data || []);
    if (supResp.status === "fulfilled") setSuppliers(supResp.value.data.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = parts.filter((p) => {
    if (!showArchived && !p.isActive) return false;
    const q = search.toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
  });

  const handleArchiveToggle = async (p) => {
    try {
      await api.patch(`/manufacturing/parts/${p.id}/${p.isActive ? "archive" : "unarchive"}`);
      setToast({ msg: p.isActive ? "Part archived" : "Part unarchived", type: "success" });
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to update part", type: "error" });
    } finally {
      setArchiveConfirm(null);
    }
  };

  const leadTimeSummary = (p) => {
    if (!p.baseQuantity || !p.baseLeadTime) return "—";
    return `${p.baseLeadTime}d per ${p.baseQuantity.toLocaleString()} units`;
  };

  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", margin: "0 0 20px", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Parts Master</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>Configure the catalogue of parts used in production orders and inventory.</p>
        </div>
        <button className="btn-primary" onClick={() => setModal("new")} style={{ whiteSpace: "nowrap" }}>
          + Add Part
        </button>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <input
            style={{ ...inputStyle, width: 280 }}
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748b", cursor: "pointer" }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            {parts.length === 0 ? "No parts yet. Add your first part to get started." : "No parts match your search."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Part</th>
                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Code</th>
                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Lead Time</th>
                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Stock</th>
                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Location</th>
                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Supplier</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid #e4ebf5", width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => (
                <tr key={p.id} style={{ borderBottom: idx < filtered.length - 1 ? "1px solid #f1f5f9" : "none", opacity: p.isActive ? 1 : 0.55 }}>
                  <td style={{ padding: "10px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {p.imageFileName ? (
                        <img
                          src={`${API_UPLOAD_URL}${p.imageFileName}`}
                          alt={p.name}
                          style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, border: "1px solid #e4ebf5", flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 6, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                          ⚙️
                        </div>
                      )}
                      <span style={{ fontWeight: 600, color: "#0f2d5c" }}>{p.name}</span>
                      {!p.isActive && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", background: "#f1f5f9", borderRadius: 999, padding: "2px 8px" }}>Archived</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "10px 16px", color: "#64748b", fontFamily: "monospace" }}>{p.code}</td>
                  <td style={{ padding: "10px 16px", color: "#374151" }}>
                    {p.baseQuantity && p.baseLeadTime ? (
                      <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
                        {leadTimeSummary(p)}
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>Not set</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <span style={{ color: p.minStockLevel != null && p.currentStock <= p.minStockLevel ? "#dc2626" : "#0f2d5c", fontWeight: 700 }}>
                      {p.currentStock ?? 0} {p.unitOfMeasure}
                    </span>
                    <StockRangeBar current={p.currentStock ?? 0} min={p.minStockLevel} max={p.maxStockLevel} />
                  </td>
                  <td style={{ padding: "10px 16px", color: "#64748b" }}>{p.location?.name || "—"}</td>
                  <td style={{ padding: "10px 16px", color: "#64748b" }}>{p.supplier?.name || "—"}</td>
                  <td style={{ padding: "10px 16px", textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={() => setModal(p)}
                      style={{ background: "none", border: "1px solid #d6dfed", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 12, color: "#374151", marginRight: 6 }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setArchiveConfirm(p)}
                      style={{ background: "none", border: `1px solid ${p.isActive ? "#fca5a5" : "#86efac"}`, borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 12, color: p.isActive ? "#dc2626" : "#15803d" }}
                    >
                      {p.isActive ? "Archive" : "Unarchive"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {modal && (
        <PartModal
          part={modal === "new" ? null : modal}
          categories={categories}
          setCategories={setCategories}
          locations={locations}
          setLocations={setLocations}
          suppliers={suppliers}
          setSuppliers={setSuppliers}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); setToast({ msg: "Part saved", type: "success" }); }}
        />
      )}

      {archiveConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 360, boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: "0 0 8px", color: "#0f2d5c" }}>
              {archiveConfirm.isActive ? "Archive" : "Unarchive"} Part?
            </h3>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#64748b" }}>
              {archiveConfirm.isActive
                ? <>Archive <strong>{archiveConfirm.name}</strong> ({archiveConfirm.code})? It will be hidden from part pickers but its history is preserved.</>
                : <>Unarchive <strong>{archiveConfirm.name}</strong> ({archiveConfirm.code})? It will become selectable again.</>}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setArchiveConfirm(null)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #d6dfed", background: "#f8fafc", cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={() => handleArchiveToggle(archiveConfirm)} style={{ padding: "8px 16px", borderRadius: 8, background: archiveConfirm.isActive ? "#dc2626" : "#15803d", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                {archiveConfirm.isActive ? "Archive" : "Unarchive"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
